/* -------------------------- Design imports start -------------------------- */
import { Grid } from '@mui/material'

/* -------------------------- Design imports end -------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import React, { type ReactElement } from "react";
import { useTranslation } from 'react-i18next'

import GetConstant from './utils/constants'
import MultilevelDrawer from '../../components/layout/MultilevelDrawer'
import { missingRequiredFormFields } from '../../utils/functions'
import { Form, type FormFieldType } from '../Form'
import { type APIProperty } from "../../generated-types";
/* ------------------------- Functional imports end ------------------------- */

type Props = {
  open: boolean
  setOpen: (open: boolean) => void
  formState: {
    state: APIProperty | undefined
    setState: React.Dispatch<React.SetStateAction<APIProperty | undefined>>
  }
  onClose?: () => void
  onCancel?: () => void
  onCreateConfirm: (a: APIProperty) => void
  enableEditMode?: boolean
  onEditConfirm?: (input: APIProperty) => void
}
/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function PropertyDrawer(props: Props): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const {
    open,
    setOpen,
    onClose = () => null,
    formState,
    onCreateConfirm = () => null,
    enableEditMode = false,
    onEditConfirm = () => alert('TODO: provide a onEditConfirm callback!'),
  } = props
  const { t } = useTranslation()
  /* --------------------------- Non state data end --------------------------- */

  /* ------------------------ Callback functions start ------------------------ */
  const handleClose = (preventOnClose = false): void => {
    // reset modal to initial state
    formState.setState(undefined)
    setOpen(false)

    !preventOnClose && onClose()
  }

  const handleCreateConfirm = (): void => {
    if (!formState.state) {
      throw new Error('formState is undefined')
    }
    // posting request to server and waiting on response
    onCreateConfirm({ ...formState.state })
    handleClose(true)
  }

  const handleEditConfirm = (): void => {
    if (!formState.state) {
      throw new Error('formState is undefined')
    }
    // posting request to server and waiting on response
    onEditConfirm(formState.state)
    handleClose(true)
  }
  /* ------------------------- Callback functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */
  const formFieldsProperty: FormFieldType[] = GetConstant({
    name: 'propertyFormFields',
  }) as FormFieldType[]
  /* -------------------------- Render constants end -------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */

  if (!formState.state) {
    return <></>
  }

  return (
    <MultilevelDrawer
      open={open}
      setOpen={setOpen}
      onClose={handleClose}
      size="small"
      title={t('item:content.heading.property') }
      confirmButtonProps={{
        text: enableEditMode
          ? t('common:interaction.button.save')
          : t('common:interaction.button.create'),
        onConfirm: () => {
          enableEditMode
            ? handleEditConfirm()
            : handleCreateConfirm()
        },
        disabled: missingRequiredFormFields(formFieldsProperty, formState.state),
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Form
            editing={true}
            formFields={formFieldsProperty}
            formObject={formState.state}
            onChange={(value: any) => formState.setState(value)}
          />
        </Grid>
      </Grid>
    </MultilevelDrawer>
  )
}
